import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  CogIcon,
  BellIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  EyeSlashIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { RootState } from '../../store';
import axios from 'axios';
import { settingsService } from '../../services/settingsService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

interface AdvancedSettings {
  notifications: {
    emailDigest: 'daily' | 'weekly' | 'never';
    creatorUpdates: boolean;
    newPosts: boolean;
    comments: boolean;
    messages: boolean;
    newsletters: boolean;
    promotions: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'patrons-only' | 'private';
    showPatronCount: boolean;
    showActivity: boolean;
    allowMessages: 'everyone' | 'patrons' | 'none';
    showInSearch: boolean;
  };
  preferences: {
    language: string;
    timezone: string;
    currency: string;
    theme: 'light' | 'dark' | 'auto';
    contentFilter: 'off' | 'moderate' | 'strict';
  };
  data: {
    allowDataCollection: boolean;
    allowPersonalization: boolean;
    allowThirdParty: boolean;
  };
}

const defaultSettings: AdvancedSettings = {
  notifications: {
    emailDigest: 'weekly',
    creatorUpdates: true,
    newPosts: true,
    comments: true,
    messages: true,
    newsletters: false,
    promotions: false,
  },
  privacy: {
    profileVisibility: 'public',
    showPatronCount: true,
    showActivity: true,
    allowMessages: 'everyone',
    showInSearch: true,
  },
  preferences: {
    language: 'en',
    timezone: 'America/New_York',
    currency: 'USD',
    theme: 'light',
    contentFilter: 'moderate',
  },
  data: {
    allowDataCollection: true,
    allowPersonalization: true,
    allowThirdParty: false,
  },
};

export default function AdvancedSettingsPage() {
  const { user } = useSelector((state: RootState) => state.auth);
  const [settings, setSettings] = useState<AdvancedSettings>(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  useEffect(() => {
    settingsService.getSettings()
      .then(setSettings)
      .catch(error => {
        console.error('Failed to load settings:', error);
      });
  }, []);

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await settingsService.updateSettings(settings);
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({
        type: 'error',
        text: 'Failed to save settings. Please try again.',
      });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== user?.username) {
      return;
    }

    setLoading(true);
    try {
      // In production, this would call the API
      await axios.delete(`${API_URL}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Clear all data and redirect
      localStorage.clear();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CogIcon className="h-8 w-8 mr-3 text-purple-600" />
                Advanced Settings
              </h1>
              <p className="text-gray-600 mt-2">
                Customize your experience with advanced preferences
              </p>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`rounded-lg p-4 mb-6 flex items-center ${
              saveMessage.type === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {saveMessage.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 mr-2" />
            ) : (
              <XMarkIcon className="h-5 w-5 mr-2" />
            )}
            {saveMessage.text}
          </div>
        )}

        {/* Notification Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <BellIcon className="h-6 w-6 mr-2 text-blue-600" />
            Notification Preferences
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Digest Frequency
              </label>
              <select
                value={settings.notifications.emailDigest}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    notifications: {
                      ...settings.notifications,
                      emailDigest: e.target.value as any,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            </div>

            {Object.entries({
              creatorUpdates: 'Creator Updates',
              newPosts: 'New Posts from Creators',
              comments: 'Comment Replies',
              messages: 'Direct Messages',
              newsletters: 'Newsletter Updates',
              promotions: 'Promotions and Offers',
            }).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between">
                <span className="text-gray-700">{label}</span>
                <input
                  type="checkbox"
                  checked={settings.notifications[key as keyof typeof settings.notifications] as boolean}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      notifications: {
                        ...settings.notifications,
                        [key]: e.target.checked,
                      },
                    })
                  }
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                />
              </label>
            ))}
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-6 w-6 mr-2 text-green-600" />
            Privacy Settings
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Visibility
              </label>
              <select
                value={settings.privacy.profileVisibility}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    privacy: {
                      ...settings.privacy,
                      profileVisibility: e.target.value as any,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="public">Public - Anyone can see</option>
                <option value="patrons-only">Patrons Only</option>
                <option value="private">Private - Hidden</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who Can Message You
              </label>
              <select
                value={settings.privacy.allowMessages}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    privacy: {
                      ...settings.privacy,
                      allowMessages: e.target.value as any,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="everyone">Everyone</option>
                <option value="patrons">Creators I Support</option>
                <option value="none">No One</option>
              </select>
            </div>

            {Object.entries({
              showPatronCount: 'Show Patron Count on Profile',
              showActivity: 'Show Activity Status',
              showInSearch: 'Appear in Search Results',
            }).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between">
                <span className="text-gray-700">{label}</span>
                <input
                  type="checkbox"
                  checked={settings.privacy[key as keyof typeof settings.privacy] as boolean}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      privacy: {
                        ...settings.privacy,
                        [key]: e.target.checked,
                      },
                    })
                  }
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                />
              </label>
            ))}
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <PaintBrushIcon className="h-6 w-6 mr-2 text-pink-600" />
            Display & Language Preferences
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={settings.preferences.language}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: {
                      ...settings.preferences,
                      language: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
                <option value="zh">中文</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={settings.preferences.timezone}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: {
                      ...settings.preferences,
                      timezone: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Seoul">Seoul (KST)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={settings.preferences.currency}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: {
                      ...settings.preferences,
                      currency: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="CAD">CAD ($)</option>
                <option value="AUD">AUD ($)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme
              </label>
              <select
                value={settings.preferences.theme}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: {
                      ...settings.preferences,
                      theme: e.target.value as any,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto (System)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content Filter
              </label>
              <select
                value={settings.preferences.contentFilter}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    preferences: {
                      ...settings.preferences,
                      contentFilter: e.target.value as any,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
              >
                <option value="off">Off - Show all content</option>
                <option value="moderate">Moderate - Filter explicit content</option>
                <option value="strict">Strict - Maximum filtering</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data & Privacy */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <GlobeAltIcon className="h-6 w-6 mr-2 text-indigo-600" />
            Data & Privacy Controls
          </h2>

          <div className="space-y-4">
            {Object.entries({
              allowDataCollection: 'Allow analytics and usage data collection',
              allowPersonalization: 'Allow personalized recommendations',
              allowThirdParty: 'Allow third-party integrations to access data',
            }).map(([key, label]) => (
              <label key={key} className="flex items-center justify-between">
                <span className="text-gray-700">{label}</span>
                <input
                  type="checkbox"
                  checked={settings.data[key as keyof typeof settings.data]}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      data: {
                        ...settings.data,
                        [key]: e.target.checked,
                      },
                    })
                  }
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                />
              </label>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t">
            <div className="flex items-start space-x-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
              <EyeSlashIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 mb-1">Your Privacy Matters</p>
                <p>
                  We respect your privacy. You can download your data or request account
                  deletion at any time. Learn more in our{' '}
                  <a href="/legal/privacy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <button
            onClick={handleSaveSettings}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>

        {/* Danger Zone */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
            Danger Zone
          </h2>
          <p className="text-red-800 mb-4">
            Once you delete your account, there is no going back. This action cannot be
            undone.
          </p>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center"
          >
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2 text-red-600" />
              Delete Account
            </h3>
            <p className="text-gray-600 mb-4">
              This action is permanent and cannot be undone. All your data, memberships,
              and content will be permanently deleted.
            </p>
            <p className="text-gray-900 font-medium mb-2">
              Type your username "{user?.username}" to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={user?.username}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 mb-4"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1 bg-gray-200 text-gray-900 px-4 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== user?.username || loading}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
